import { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Palmtree, Thermometer, Upload, Send, Clock, CheckCircle2, XCircle, FileText } from 'lucide-react';
import { toast } from 'sonner';

const API = process.env.REACT_APP_BACKEND_URL + '/api';

const statoBadge = {
  in_attesa: { label: 'In Attesa', className: 'bg-amber-100 text-amber-700 border-amber-300' },
  approvata: { label: 'Approvata', className: 'bg-emerald-100 text-emerald-700 border-emerald-300' },
  rifiutata: { label: 'Rifiutata', className: 'bg-red-100 text-red-700 border-red-300' },
  ricevuto: { label: 'Ricevuto', className: 'bg-blue-100 text-blue-700 border-blue-300' },
};

export default function WorkerFerieMalattia() {
  const [ferieForm, setFerieForm] = useState({ data_inizio: '', data_fine: '', note: '' });
  const [permessoForm, setPermessoForm] = useState({ data_inizio: '', data_fine: '', ore_permesso: '', note: '' });
  const [malattiaForm, setMalattiaForm] = useState({ data_inizio: '', data_fine: '', protocollo_inps: '', note: '' });
  const [file, setFile] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [richiesteFerie, setRichiesteFerie] = useState([]);
  const [richiestePermesso, setRichiestePermesso] = useState([]);
  const [certificati, setCertificati] = useState([]);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);

  const fetchAll = useCallback(async () => {
    setLoading(true);
    try {
      const [ferieRes, permRes, malattiaRes] = await Promise.all([
        axios.get(`${API}/richieste-ferie`, { withCredentials: true }),
        axios.get(`${API}/richieste-permesso`, { withCredentials: true }),
        axios.get(`${API}/certificati-malattia`, { withCredentials: true })
      ]);
      setRichiesteFerie(ferieRes.data);
      setRichiestePermesso(permRes.data);
      setCertificati(malattiaRes.data);
    } catch (err) {
      console.error('Errore caricamento richieste worker:', err);
      toast.error('Errore nel caricamento');
    }
    finally { setLoading(false); }
  }, []);

  useEffect(() => { fetchAll(); }, [fetchAll]);

  const handleSubmitFerie = async (e) => {
    e.preventDefault();
    if (!ferieForm.data_inizio || !ferieForm.data_fine) { toast.error('Inserisci le date'); return; }
    if (ferieForm.data_fine < ferieForm.data_inizio) { toast.error('La data fine deve essere dopo la data inizio'); return; }
    setSubmitting(true);
    try {
      await axios.post(`${API}/richieste-ferie`, ferieForm, { withCredentials: true });
      toast.success('Richiesta ferie inviata');
      setFerieForm({ data_inizio: '', data_fine: '', note: '' });
      fetchAll();
    } catch { toast.error('Errore nell\'invio'); }
    finally { setSubmitting(false); }
  };

  const handleSubmitPermesso = async (e) => {
    e.preventDefault();
    if (!permessoForm.data_inizio || !permessoForm.data_fine) { toast.error('Inserisci le date'); return; }
    setSubmitting(true);
    try {
      await axios.post(`${API}/richieste-permesso`, {
        ...permessoForm,
        ore_permesso: permessoForm.ore_permesso ? parseFloat(permessoForm.ore_permesso) : 0
      }, { withCredentials: true });
      toast.success('Richiesta permesso inviata');
      setPermessoForm({ data_inizio: '', data_fine: '', ore_permesso: '', note: '' });
      fetchAll();
    } catch { toast.error('Errore nell\'invio'); }
    finally { setSubmitting(false); }
  };

  const uploadFile = async () => {
    if (!file) return '';
    setUploading(true);
    try {
      const formData = new FormData();
      formData.append('file', file);
      const res = await axios.post(`${API}/upload`, formData, { withCredentials: true, headers: { 'Content-Type': 'multipart/form-data' } });
      const path = res.data?.path;
      return path ? `/api/files/${path}` : '';
    } catch { toast.error('Errore upload file'); return ''; }
    finally { setUploading(false); }
  };

  const handleSubmitMalattia = async (e) => {
    e.preventDefault();
    if (!malattiaForm.data_inizio || !malattiaForm.data_fine || !malattiaForm.protocollo_inps) {
      toast.error('Compila tutti i campi obbligatori'); return;
    }
    setSubmitting(true);
    try {
      let file_url = '';
      if (file) { file_url = await uploadFile(); }
      await axios.post(`${API}/certificati-malattia`, { ...malattiaForm, file_url }, { withCredentials: true });
      toast.success('Certificato malattia inviato');
      setMalattiaForm({ data_inizio: '', data_fine: '', protocollo_inps: '', note: '' });
      setFile(null);
      fetchAll();
    } catch { toast.error('Errore nell\'invio'); }
    finally { setSubmitting(false); }
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return '';
    const head = String(dateStr).slice(0, 10).replace(/\//g, '-');
    const parts = head.split('-');
    if (parts.length !== 3 || parts.some(p => !p)) return String(dateStr);
    const [y, m, d] = parts;
    return `${d}-${m}-${y}`;
  };

  const countDays = (start, end) => {
    if (!start || !end) return 0;
    const s = new Date(start);
    const e = new Date(end);
    let count = 0;
    let current = new Date(s);
    while (current <= e) {
      if (current.getDay() !== 0 && current.getDay() !== 6) count++;
      current.setDate(current.getDate() + 1);
    }
    return count;
  };

  const renderRequestList = (items, type) => {
    if (items.length === 0) return <p className="text-sm text-slate-400 text-center py-6">Nessuna richiesta</p>;
    const iconMap = { ferie: Palmtree, permesso: Clock, malattia: Thermometer };
    const colorMap = { ferie: 'text-blue-500', permesso: 'text-purple-500', malattia: 'text-amber-500' };
    const Icon = iconMap[type] || Clock;
    const color = colorMap[type] || 'text-slate-500';

    return items.map(r => (
      <Card key={r.id} className="border-slate-200" data-testid={`richiesta-${type}-${r.id}`}>
        <CardContent className="p-4">
          <div className="flex items-start justify-between">
            <div className="space-y-1">
              <div className="flex items-center gap-2">
                <Icon className={`h-4 w-4 ${color}`} />
                <span className="font-medium text-sm">{formatDate(r.data_inizio)} - {formatDate(r.data_fine)}</span>
              </div>
              <p className="text-xs text-slate-500">{countDays(r.data_inizio, r.data_fine)} giorni lavorativi</p>
              {r.ore_permesso > 0 && <p className="text-xs text-purple-600">{r.ore_permesso}h di permesso</p>}
              {r.protocollo_inps && <p className="text-xs text-slate-600">Prot: <strong>{r.protocollo_inps}</strong></p>}
              {r.note && <p className="text-xs text-slate-500 mt-1">{r.note}</p>}
              {r.file_url && (
                <a href={r.file_url} target="_blank" rel="noopener noreferrer" className="text-xs text-blue-600 hover:underline flex items-center gap-1">
                  <FileText className="h-3 w-3" />Vedi certificato
                </a>
              )}
            </div>
            <Badge variant="outline" className={statoBadge[r.stato]?.className || ''}>
              {r.stato === 'in_attesa' && <Clock className="h-3 w-3 mr-1" />}
              {r.stato === 'approvata' && <CheckCircle2 className="h-3 w-3 mr-1" />}
              {r.stato === 'rifiutata' && <XCircle className="h-3 w-3 mr-1" />}
              {statoBadge[r.stato]?.label || r.stato}
            </Badge>
          </div>
        </CardContent>
      </Card>
    ));
  };

  if (loading) return <div className="flex items-center justify-center h-64"><div className="animate-spin rounded-full h-8 w-8 border-b-2 border-slate-900"></div></div>;

  return (
    <div className="space-y-6" data-testid="worker-ferie-malattia-page">
      <div>
        <h1 className="text-2xl font-bold text-slate-900">Ferie, Permessi & Malattia</h1>
        <p className="text-sm text-slate-500 mt-1">Richiedi ferie/permessi o invia certificati medici</p>
      </div>

      <Tabs defaultValue="ferie" className="w-full">
        <TabsList className="w-full grid grid-cols-3">
          <TabsTrigger value="ferie" className="flex items-center gap-1.5" data-testid="tab-ferie">
            <Palmtree className="h-4 w-4" />Ferie
          </TabsTrigger>
          <TabsTrigger value="permesso" className="flex items-center gap-1.5" data-testid="tab-permesso">
            <Clock className="h-4 w-4" />Permessi
          </TabsTrigger>
          <TabsTrigger value="malattia" className="flex items-center gap-1.5" data-testid="tab-malattia">
            <Thermometer className="h-4 w-4" />Malattia
          </TabsTrigger>
        </TabsList>

        {/* FERIE TAB */}
        <TabsContent value="ferie" className="space-y-4 mt-4">
          <Card className="border-blue-200 bg-blue-50/30">
            <CardHeader className="pb-3">
              <CardTitle className="text-base flex items-center gap-2">
                <Palmtree className="h-5 w-5 text-blue-600" />Richiedi Ferie
              </CardTitle>
            </CardHeader>
            <CardContent>
              <form onSubmit={handleSubmitFerie} className="space-y-4">
                <div className="grid grid-cols-2 gap-3">
                  <div className="space-y-1.5">
                    <Label className="text-xs">Dal</Label>
                    <Input type="date" value={ferieForm.data_inizio} onChange={(e) => setFerieForm({...ferieForm, data_inizio: e.target.value})} data-testid="ferie-data-inizio" />
                  </div>
                  <div className="space-y-1.5">
                    <Label className="text-xs">Al</Label>
                    <Input type="date" value={ferieForm.data_fine} onChange={(e) => setFerieForm({...ferieForm, data_fine: e.target.value})} data-testid="ferie-data-fine" />
                  </div>
                </div>
                {ferieForm.data_inizio && ferieForm.data_fine && ferieForm.data_fine >= ferieForm.data_inizio && (
                  <p className="text-xs text-blue-600 font-medium" data-testid="ferie-day-count">{countDays(ferieForm.data_inizio, ferieForm.data_fine)} giorni lavorativi</p>
                )}
                <div className="space-y-1.5">
                  <Label className="text-xs">Note (opzionale)</Label>
                  <Textarea value={ferieForm.note} onChange={(e) => setFerieForm({...ferieForm, note: e.target.value})} placeholder="Motivazione..." rows={2} data-testid="ferie-note" />
                </div>
                <Button type="submit" disabled={submitting} className="w-full bg-blue-600 hover:bg-blue-700 text-white" data-testid="submit-ferie-btn">
                  <Send className="h-4 w-4 mr-2" />{submitting ? 'Invio...' : 'Invia Richiesta'}
                </Button>
              </form>
            </CardContent>
          </Card>
          <div className="space-y-3">
            <h3 className="text-sm font-semibold text-slate-700">Le tue richieste ferie</h3>
            {renderRequestList(richiesteFerie, 'ferie')}
          </div>
        </TabsContent>

        {/* PERMESSI TAB */}
        <TabsContent value="permesso" className="space-y-4 mt-4">
          <Card className="border-purple-200 bg-purple-50/30">
            <CardHeader className="pb-3">
              <CardTitle className="text-base flex items-center gap-2">
                <Clock className="h-5 w-5 text-purple-600" />Richiedi Permesso
              </CardTitle>
            </CardHeader>
            <CardContent>
              <form onSubmit={handleSubmitPermesso} className="space-y-4">
                <div className="grid grid-cols-2 gap-3">
                  <div className="space-y-1.5">
                    <Label className="text-xs">Dal</Label>
                    <Input type="date" value={permessoForm.data_inizio} onChange={(e) => setPermessoForm({...permessoForm, data_inizio: e.target.value})} data-testid="permesso-data-inizio" />
                  </div>
                  <div className="space-y-1.5">
                    <Label className="text-xs">Al</Label>
                    <Input type="date" value={permessoForm.data_fine} onChange={(e) => setPermessoForm({...permessoForm, data_fine: e.target.value})} data-testid="permesso-data-fine" />
                  </div>
                </div>
                <div className="space-y-1.5">
                  <Label className="text-xs">Ore di permesso (opzionale)</Label>
                  <Input type="number" step="0.5" min="0" max="8" value={permessoForm.ore_permesso} onChange={(e) => setPermessoForm({...permessoForm, ore_permesso: e.target.value})} placeholder="Es: 2" data-testid="permesso-ore" />
                </div>
                <div className="space-y-1.5">
                  <Label className="text-xs">Motivazione</Label>
                  <Textarea value={permessoForm.note} onChange={(e) => setPermessoForm({...permessoForm, note: e.target.value})} placeholder="Motivo del permesso..." rows={2} data-testid="permesso-note" />
                </div>
                <Button type="submit" disabled={submitting} className="w-full bg-purple-600 hover:bg-purple-700 text-white" data-testid="submit-permesso-btn">
                  <Send className="h-4 w-4 mr-2" />{submitting ? 'Invio...' : 'Invia Richiesta'}
                </Button>
              </form>
            </CardContent>
          </Card>
          <div className="space-y-3">
            <h3 className="text-sm font-semibold text-slate-700">I tuoi permessi</h3>
            {renderRequestList(richiestePermesso, 'permesso')}
          </div>
        </TabsContent>

        {/* MALATTIA TAB */}
        <TabsContent value="malattia" className="space-y-4 mt-4">
          <Card className="border-amber-200 bg-amber-50/30">
            <CardHeader className="pb-3">
              <CardTitle className="text-base flex items-center gap-2">
                <Thermometer className="h-5 w-5 text-amber-600" />Invia Certificato Malattia
              </CardTitle>
            </CardHeader>
            <CardContent>
              <form onSubmit={handleSubmitMalattia} className="space-y-4">
                <div className="grid grid-cols-2 gap-3">
                  <div className="space-y-1.5">
                    <Label className="text-xs">Dal</Label>
                    <Input type="date" value={malattiaForm.data_inizio} onChange={(e) => setMalattiaForm({...malattiaForm, data_inizio: e.target.value})} data-testid="malattia-data-inizio" />
                  </div>
                  <div className="space-y-1.5">
                    <Label className="text-xs">Al</Label>
                    <Input type="date" value={malattiaForm.data_fine} onChange={(e) => setMalattiaForm({...malattiaForm, data_fine: e.target.value})} data-testid="malattia-data-fine" />
                  </div>
                </div>
                <div className="space-y-1.5">
                  <Label className="text-xs">Numero Protocollo INPS *</Label>
                  <Input value={malattiaForm.protocollo_inps} onChange={(e) => setMalattiaForm({...malattiaForm, protocollo_inps: e.target.value})} placeholder="Es: 1234567890" data-testid="malattia-protocollo" />
                </div>
                <div className="space-y-1.5">
                  <Label className="text-xs">Certificato Medico (PDF/immagine)</Label>
                  <div className="flex items-center gap-2">
                    <Input type="file" accept=".pdf,.jpg,.jpeg,.png,.heic" onChange={(e) => setFile(e.target.files?.[0] || null)} className="text-xs" data-testid="malattia-file-input" />
                    {file && <Badge variant="outline" className="text-xs whitespace-nowrap"><FileText className="h-3 w-3 mr-1" />{file.name.slice(0, 15)}...</Badge>}
                  </div>
                </div>
                <div className="space-y-1.5">
                  <Label className="text-xs">Note (opzionale)</Label>
                  <Textarea value={malattiaForm.note} onChange={(e) => setMalattiaForm({...malattiaForm, note: e.target.value})} placeholder="Dettagli..." rows={2} data-testid="malattia-note" />
                </div>
                <Button type="submit" disabled={submitting || uploading} className="w-full bg-amber-600 hover:bg-amber-700 text-white" data-testid="submit-malattia-btn">
                  <Upload className="h-4 w-4 mr-2" />{submitting || uploading ? 'Invio...' : 'Invia Certificato'}
                </Button>
              </form>
            </CardContent>
          </Card>
          <div className="space-y-3">
            <h3 className="text-sm font-semibold text-slate-700">I tuoi certificati</h3>
            {renderRequestList(certificati, 'malattia')}
          </div>
        </TabsContent>
      </Tabs>
    </div>
  );
}
